import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Mail, Send, CheckCircle2, Wrench, Lightbulb, CreditCard } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Seo } from "../components/Seo";
import { http } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TOPICS = [
  { value: "technical", label: "Technical Assistance", icon: Wrench },
  { value: "improvement", label: "Suggested improvement", icon: Lightbulb },
  { value: "subscription", label: "Subscription or pricing query", icon: CreditCard },
];

const EMPTY = { name: "", email: "", topic: "", message: "", website: "" };

export default function Contact() {
  const { user } = useAuth();
  const [form, setForm] = useState(() => ({ ...EMPTY, name: user?.name || "", email: user?.email || "" }));
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "" }));
  }, [user]);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
    setErrors((er) => (er[k] ? { ...er, [k]: undefined } : er));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your full name.";
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) e.email = "Please enter a valid email address.";
    if (!form.topic) e.topic = "Please choose a topic.";
    if (!form.message.trim()) e.message = "Please write a message.";
    setErrors(e);
    return !Object.keys(e).length;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await http.post("/contact", { ...form, name: form.name.trim(), email: form.email.trim() });
      setSent(true);
      setForm({ ...EMPTY, name: user?.name || "", email: user?.email || "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail?.toString?.() || "Could not send your message. Please try again.");
    } finally { setSending(false); }
  };

  return (
    <>
      <Seo title="Contact us" path="/contact" description="Get in touch with Framer's Bench — technical assistance, suggested improvements, or subscription and pricing questions." />
      <div className="mx-auto grid max-w-6xl gap-12 px-4 py-14 sm:px-6 lg:grid-cols-[1fr_1.3fr] lg:px-8">
        <ContactIntro />
        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8" data-testid="contact-card">
          {sent ? (
            <SentState onAgain={() => setSent(false)} />
          ) : (
            <form onSubmit={submit} noValidate className="space-y-5" data-testid="contact-form">
              <Field id="contact-name" label="Full name" error={errors.name}>
                <Input id="contact-name" value={form.name} onChange={set("name")} maxLength={120} autoComplete="name" placeholder="Jane Smith" data-testid="contact-name-input" />
              </Field>
              <Field id="contact-email" label="Email address" error={errors.email}>
                <Input id="contact-email" type="email" value={form.email} onChange={set("email")} autoComplete="email" placeholder="you@example.com" data-testid="contact-email-input" />
              </Field>
              <Field id="contact-topic" label="What can we help with?" error={errors.topic}>
                <Select value={form.topic} onValueChange={set("topic")}>
                  <SelectTrigger id="contact-topic" data-testid="contact-topic-select"><SelectValue placeholder="Choose a topic" /></SelectTrigger>
                  <SelectContent>
                    {TOPICS.map((t) => (
                      <SelectItem key={t.value} value={t.value} data-testid={`contact-topic-${t.value}`}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field id="contact-message" label="Message" error={errors.message}>
                <Textarea id="contact-message" rows={7} value={form.message} onChange={set("message")} maxLength={5000} placeholder="Tell us a little about your question or idea…" data-testid="contact-message-input" />
                <p className="mt-1 text-right text-xs text-muted-foreground">{form.message.length}/5000</p>
              </Field>
              <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden="true" />
              <Button type="submit" size="lg" className="w-full gap-2" disabled={sending} data-testid="contact-submit-btn">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {sending ? "Sending…" : "Send message"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </>
  );
}

function Field({ id, label, error, children }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {error && <p className="text-xs text-destructive" data-testid={`${id}-error`}>{error}</p>}
    </div>
  );
}

function ContactIntro() {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-primary">Contact</p>
      <h1 className="mt-3 font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">Talk to the bench.</h1>
      <p className="mt-4 max-w-md text-base leading-relaxed text-muted-foreground">
        Stuck on a measurement, have an idea that would make your workflow faster, or a question about your subscription?
        Send us a note and we'll get back to you by email.
      </p>
      <ul className="mt-8 space-y-4">
        {TOPICS.map(({ value, label, icon: Icon }) => (
          <li key={value} className="flex items-center gap-3 text-sm">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary"><Icon className="h-4 w-4" /></span>
            {label}
          </li>
        ))}
      </ul>
      <p className="mt-8 flex items-center gap-2 text-sm text-muted-foreground">
        <Mail className="h-4 w-4" /> Prefer email? <a href="mailto:info@framersbench.com" className="font-medium text-foreground hover:text-primary" data-testid="contact-mailto-link">info@framersbench.com</a>
      </p>
      <p className="mt-2 text-sm text-muted-foreground">
        Pricing questions? Check our <Link to="/pricing" className="font-medium text-foreground hover:text-primary">pricing FAQ</Link> first.
      </p>
    </div>
  );
}

function SentState({ onAgain }) {
  return (
    <div className="py-10 text-center" data-testid="contact-success">
      <CheckCircle2 className="mx-auto h-12 w-12 text-primary" />
      <h2 className="mt-4 font-heading text-2xl font-bold" data-testid="contact-success-title">Your message has been sent.</h2>
      <p className="mx-auto mt-2 max-w-sm text-base text-muted-foreground" data-testid="contact-success-text">We will reply to you as soon as possible.</p>
      <Button variant="outline" className="mt-6" onClick={onAgain} data-testid="contact-send-another">Send another message</Button>
    </div>
  );
}
